import { Link } from 'react-router-dom';
import { DocsNav } from '../components/DocsNav';
import {
  Card,
  CommandBlock,
  Eyebrow,
  Heading,
  Lead,
  ScrollTable,
  Section,
} from '../components/ui';
import { usePageTitle } from '../lib/usePageTitle';

/**
 * The complete command reference.
 *
 * Getting started shows the five commands that take someone from an empty
 * machine to a first report, in the order they are needed. That is the right
 * shape for a first read and the wrong shape for the second one: a QA engineer
 * who already has a report and wants to know how to re-open it, or which of
 * their features still lack a spec, has nowhere to look — the answer is a
 * command that getting-started never had a reason to mention.
 *
 * So this page is organised by the question being asked rather than by
 * alphabet, and every entry says when you would reach for it, not only what it
 * prints. `waveqa guide` prints the same grouping in the terminal; the two are
 * meant to be recognisably the same document.
 */

type Command = {
  /** As typed. */
  name: string;
  /** What it does, in one line. */
  summary: string;
  /** When a QA engineer would reach for it, and what to know before doing so. */
  detail: string;
  /** Options worth knowing, as `flag` / `what it does`. */
  options?: { flag: string; does: string }[];
  /** A copyable example. */
  example?: string;
  /** New in this release, so a returning reader can find what changed. */
  isNew?: boolean;
};

type Group = {
  id: string;
  title: string;
  lead: string;
  commands: Command[];
};

const GROUPS: Group[] = [
  {
    id: 'setup',
    title: 'Set the machine up',
    lead: 'Run once. Come back to these when a credential expires, the model changes, or a run starts behaving oddly.',
    commands: [
      {
        name: 'waveqa setup',
        summary: 'The configuration wizard: credentials, working folder and language model.',
        detail:
          'Start here on a new machine. Create an agent credential in your dashboard first — the client secret is shown once, so copy it before closing the dialog. The wizard also asks for a working folder, which becomes the boundary every path in the agent is checked against: nothing outside it is ever read or written.',
        example: 'waveqa setup',
      },
      {
        name: 'waveqa setup portal',
        summary: 'The same wizard as a local web page.',
        detail:
          'Identical settings, entered in a browser instead of a terminal. It runs on this machine and is reachable only from this machine. Useful when you are pasting long keys, which terminals handle badly.',
        example: 'waveqa setup portal',
      },
      {
        name: 'waveqa llm setup',
        summary: 'Change the model, or its API key, without re-running the whole wizard.',
        detail:
          'The model is the one setting that changes on somebody else’s schedule: keys expire, quotas run out, a provider retires a model id. This changes it in isolation and leaves your credentials and working folder alone.',
        example: 'waveqa llm setup',
      },
      {
        name: 'waveqa doctor',
        summary: 'Check everything a run depends on, and say which part is broken.',
        detail:
          'The first thing to run when a run misbehaves. It checks the agent version, the Python runtime, credentials, where credentials are stored, the working folder, the browser runtime, API connectivity and a live reply from your configured model. Each check reports on its own line, so "it does not work" becomes one named thing that does not work. The network checks always run: reaching the API and getting an answer from the model are the two questions people actually run it for.',
        example: 'waveqa doctor',
      },
    ],
  },
  {
    id: 'author',
    title: 'Write what will be verified',
    lead: 'You write two documents. They are the only two the agent reads, and neither is ever generated for you — verifying against a template would produce a confident report about the template.',
    commands: [
      {
        name: 'waveqa templates',
        summary: 'Show the shape of the two documents you write.',
        detail:
          'A reference to read, never a file to start from. Pass a document name to print it in full, or --out to save a copy somewhere for reference. A project is deliberately not seeded with them: an unedited template on disk is indistinguishable from a document somebody began and abandoned, and validation refuses to verify against either.',
        options: [
          { flag: '<document>', does: 'Print one template in full, e.g. feature-spec.md.' },
          { flag: '--out <path>', does: 'Write it to a file instead of printing it.' },
        ],
        example: 'waveqa templates\n\nwaveqa templates feature-spec.md',
      },
      {
        name: 'waveqa -p "<project>"',
        summary: 'Create the project folder, then stop so you can write its readme.',
        detail:
          'The readme describes the product: what it is, the "## Environments" table the run reads its URLs from, and the accounts a run is permitted to sign in with. Until it exists, a run refuses rather than guessing. One readme per product.',
        example: 'waveqa -p "Checkout"',
      },
      {
        name: 'waveqa -f "<feature>"',
        summary: 'Create the feature folder, then stop so you can write its spec.',
        detail:
          'The feature spec holds the acceptance criteria this feature is judged against — it is the document that decides what a run checks, and the one thing the agent cannot write for you. It takes the project from the folder you are standing in, so the usual sequence is to cd into the project once and stay there. Features live under the project’s features/ folder, so listing them is reading one directory rather than guessing which folders are features.',
        example: 'cd Checkout\nwaveqa -f "Feature-01"',
      },
    ],
  },
  {
    id: 'run',
    title: 'Run a verification',
    lead: 'One command per feature per environment. Nothing runs unless you ask for it, and a run you can watch is the default.',
    commands: [
      {
        name: 'waveqa -p "<project>" -f "<feature>"',
        summary: 'Verify one feature against one deployment.',
        detail:
          'Every verification runs the web page QA harness — your acceptance criteria, checked against the running application. It is included on every plan, has no flag, and cannot be turned off, and the report gives it its own section saying what it reached and how it went. The run opens a browser you can watch, announces each acceptance criterion on screen before it checks it, and prints its reasoning in the terminal as it goes: the plan for each criterion, why each step was chosen, what was observed, and the verdict. Everything it decides is also written to execution-log.md in the run folder, so the terminal and the record cannot disagree. The environment decides both the address opened and the only origins the run may contact — a staging run cannot reach production.',
        options: [
          { flag: '--dev / --staging / --uat / --prod', does: 'Which deployment, read from the readme’s environments table. Defaults to dev.' },
          { flag: '--env <name>', does: 'The same choice, written out.' },
          { flag: '--headless', does: 'No window. For pipelines, where there is no screen to render to.' },
          { flag: '--pace slow|normal|fast', does: 'How long each criterion is held on screen. Changes nothing about what is checked.' },
          { flag: '--checks deterministic|llm|both', does: 'Which checking method settles each criterion. deterministic makes no model call; llm runs only what the model proposes, giving up the deterministic coverage floor; both is the default.' },
          { flag: '--security', does: 'Add the security suites that apply to any web application. Sold from Basic — a run that asks for a suite your plan lacks is refused before anything is metered.' },
          { flag: '--uwas --cwsh --owasp --gdpr --hipaa --pci …', does: 'Add one framework’s applicable controls.' },
          { flag: '--compliance-all', does: 'Evaluate every framework; irrelevant ones are reported NOT_APPLICABLE rather than run.' },
          { flag: '--active-probes', does: 'Authorise checks that send input the application did not ask for. Needs the project’s waveqa.yml to agree, and is refused against production.' },
        ],
        example:
          'waveqa -p "Checkout" -f "Feature-01" --dev\n\n# with security suites\nwaveqa -p "Checkout" -f "Feature-01" --dev --security --pci',
      },
    ],
  },
  {
    id: 'inspect',
    title: 'Read what happened',
    lead: 'Past runs, project and feature listings, and the compliance control repository. All of it answered from this machine: run output — evidence, screenshots, reports — is written here and stays here, so there is no server that could answer these.',
    commands: [
      {
        name: 'waveqa projects',
        summary: 'Every project in your working folder, and whether its documents are ready.',
        detail:
          'A project is any folder with a project.json in it, which is the same rule every other command uses to find one — so a project you created by hand appears here too. The documents column tells you which projects would refuse to run today, before you try.',
        example: 'waveqa projects',
        isNew: true,
      },
      {
        name: 'waveqa features',
        summary: 'Every feature in a project: spec present, runs so far, when it last ran.',
        detail:
          'A feature is ready to verify when its feature-spec.md exists; without one there is nothing to verify against. Takes the project from the folder you are standing in, or name one with -p.',
        options: [{ flag: '-p, --project <name>', does: 'Which project, if you are not standing in it.' }],
        example: 'waveqa features\n\nwaveqa features -p "Checkout"',
        isNew: true,
      },
      {
        name: 'waveqa report',
        summary: 'Past runs for a feature, newest first, with where each report was written.',
        detail:
          'One row per run: when it ran, its verdict, which environment it was aimed at, how many criteria passed, and which report formats reached disk. A run interrupted halfway shows as a row with blanks rather than breaking the listing. Use --open to open the newest one — the PDF if it was produced, the HTML if not, and the Markdown, which is always written.',
        options: [
          { flag: '-p, --project <name>', does: 'Which project. Defaults to the one you are in.' },
          { flag: '-f, --feature <name>', does: 'Which feature. Defaults to the only one.' },
          { flag: '--open', does: 'Open the newest report instead of listing runs.' },
          { flag: '-n, --limit <count>', does: 'How many runs to list. Defaults to 10.' },
        ],
        example: 'waveqa report\n\nwaveqa report --open',
        isNew: true,
      },
      {
        name: 'waveqa compliance frameworks',
        summary: 'Which frameworks this build can verify, the flag for each, and how many controls it ships.',
        detail:
          'Read from the control repository installed with your agent rather than from documentation, so it is always what your build will actually do. The counts are of controls shipped, not of controls a given run would evaluate: what runs is decided per page by applicability, and a framework that does not apply to your application resolves to one NOT_APPLICABLE block rather than a wall of verdicts.',
        example: 'waveqa compliance frameworks',
        isNew: true,
      },
      {
        name: 'waveqa compliance controls <framework>',
        summary: 'One framework’s controls, and which of them automation can settle at all.',
        detail:
          'The "settled by" column is the honest part, and it is worth reading before a run rather than after. Automated verification cannot establish a policy, a training record or a supplier review — most of ISO/IEC 27001 is exactly that — so those controls are named here up front and reported EVIDENCE_REQUIRED or NOT_UI_TESTABLE in the run, never passed.',
        options: [
          { flag: '<framework>', does: 'The framework id: owasp, hipaa, cwsh, iso27001 …' },
          { flag: '--category <name>', does: 'Only the controls in one category.' },
        ],
        example: 'waveqa compliance controls owasp\n\nwaveqa compliance controls cwsh --category Transport',
        isNew: true,
      },
      {
        name: 'waveqa guide',
        summary: 'This page, in your terminal.',
        detail:
          'Every command grouped by what you are trying to do. Printed automatically when you run waveqa with no arguments, because someone who has just typed waveqa does not yet know which command they need.',
        example: 'waveqa guide',
        isNew: true,
      },
    ],
  },
  {
    id: 'ai',
    title: 'Teach WaveQA to answer for itself',
    lead:
      'WaveQA can learn from its own runs and train a language model of its own, on your machine, from nothing. It is included from Standard upward and is off until you turn it on — it collects traces from your application and spends your CPU training, and neither should start because an agent was upgraded. Everything it collects and everything it builds stays in your project folder.',
    commands: [
      {
        name: 'waveqa ai enable',
        summary: 'Start learning from your runs.',
        detail:
          'Checks your plan with the WaveQA API before it writes anything, so a plan without the capability is refused here rather than accepted and then silently ignored on every run — which is the failure that is hardest to notice, because nothing looks broken. Turning it on is a preference, not a licence: every run checks the same entitlement again.',
        example: 'waveqa ai enable',
        isNew: true,
      },
      {
        name: 'waveqa ai disable',
        summary: 'Stop. Runs go back to the remote teacher only.',
        detail:
          'For the machine where background training is unwelcome — a laptop on battery, a shared build agent, a run that has to stay strictly reproducible. Nothing already collected is deleted, and it is used again if you re-enable. Asks the server nothing: turning a capability off never needs permission.',
        example: 'waveqa ai disable',
        isNew: true,
      },
      {
        name: 'waveqa ai status',
        summary: 'What the runtime is running, and what it has learned from.',
        detail:
          'The model in use if there is one — its size, its digest, the dataset it was trained on — and a per-project count of what has been collected, split into trusted, hard and rejected. Read from files in your own working folder; it sends nothing and asks nothing.',
        options: [{ flag: '-p, --project <name>', does: 'One project. Defaults to all of them.' }],
        example: 'waveqa ai status',
        isNew: true,
      },
      {
        name: 'waveqa ai train',
        summary: 'Whether a new model is due, and why not if it is not.',
        detail:
          'Explains rather than trains, which is the default on purpose: WaveQA trains by itself at the end of a run when the thresholds are met, and a manual run is for looking. The answer is usually that it is still collecting — a model is built from a thousand trusted examples, because training on thin data produces a candidate that fails its own benchmark. Training runs on the CPU whatever hardware is present, so --profile is what decides how long it takes: development is about 0.1M parameters and finishes in seconds, cpu-small about 1.9M and takes minutes, cpu-medium about 7.7M and takes tens of minutes, and gpu-small about 25.8M — 1,536 times the compute of development — which is hours until a GPU backend exists.',
        options: [
          { flag: '--run', does: 'Train now if eligible, instead of explaining.' },
          {
            flag: '--force',
            does: 'With --run: train on whatever has been collected, ignoring the thresholds. The result is under-trained and still has to pass the benchmark gate before anything uses it.',
          },
          {
            flag: '--profile <size>',
            does:
              'How big a model to build: development, cpu-small, cpu-medium or gpu-small. Everything runs on the CPU, so this is the setting that decides whether training takes minutes or hours — see the table below. Defaults to what the machine’s memory suggests.',
          },
          { flag: '-p, --project <name>', does: 'One project. Defaults to all of them.' },
        ],
        example: 'waveqa ai train\n\nwaveqa ai train --run\n\nwaveqa ai train --run --profile cpu-small',
        isNew: true,
      },
      {
        name: 'waveqa ai models',
        summary: 'Which models this machine holds, and which one is serving.',
        detail:
          'One row per model with its state, its digest and what it scored. A model is only ever promoted after a held-out benchmark says it earned it, and a promoted model that starts failing is rolled back to its predecessor without anyone being asked.',
        example: 'waveqa ai models',
        isNew: true,
      },
      {
        name: 'waveqa ai benchmark',
        summary: 'Score the active model against data it was never trained on.',
        detail:
          'Reports how the held-out split was made, because that changes what the number means: separating whole projects is strong evidence, and separating individual criteria is much weaker. It also names where the labels came from — a benchmark labelled by the teacher measures agreement with the teacher, not correctness.',
        example: 'waveqa ai benchmark',
        isNew: true,
      },
      {
        name: 'waveqa ai health',
        summary: 'Is the runtime working, and is anything it holds damaged.',
        detail:
          'Hardware fitness, the model registry, artefact integrity and the knowledge index. A vector index can drift apart from the text it describes in a way nothing else would report — every search then returns a plausible score attached to the wrong content — so it is checked explicitly. Exits non-zero when it finds something, so a scheduled check can read it.',
        example: 'waveqa ai health',
        isNew: true,
      },
      {
        name: 'waveqa ai rebuild-index',
        summary: 'Rebuild the local knowledge index from your documents.',
        detail:
          'Normally unnecessary: the index notices a changed document by its content hash and re-embeds only what moved, so nobody has to run an indexer. Reach for this when ai health reports drift, or after an upgrade that changed how documents are split.',
        example: 'waveqa ai rebuild-index',
        isNew: true,
      },
      {
        name: 'waveqa ai diagnostics',
        summary: 'Versions and counts for a bug report.',
        detail:
          'Every version that could change what a trace means, plus what has been collected and why it was filed where it was. It prints no trace content, no document text and no credential — the point is that it is safe to paste into a support thread without reading it first.',
        example: 'waveqa ai diagnostics',
        isNew: true,
      },
    ],
  },
  {
    id: 'maintain',
    title: 'Maintain the install',
    lead: 'Occasional housekeeping. None of these touch your projects unless the command says so in its own output first.',
    commands: [
      {
        name: 'waveqa version',
        summary: 'Which agent this is.',
        detail: 'The first thing to quote in a bug report, and the fastest way to tell whether an update actually landed.',
        example: 'waveqa version',
      },
      {
        name: 'waveqa update',
        summary: 'Install the newest signed release.',
        detail:
          'Fetches the release manifest, checks its Ed25519 signature against the key built into your agent, verifies the download against the signed hash, and only then installs. It refuses an unsigned manifest, a modified artefact, and any release older than the one you have.',
        options: [{ flag: '--check', does: 'Report what is available without installing it.' }],
        example: 'waveqa update --check\n\nwaveqa update',
      },
      {
        name: 'waveqa credentials status',
        summary: 'Whether the stored credentials are still accepted.',
        detail: 'Shows the client id and where the secret is stored, masked. It never prints the secret itself.',
        example: 'waveqa credentials status',
      },
      {
        name: 'waveqa credentials rotate',
        summary: 'Replace the client secret on this machine.',
        detail: 'Run this if a secret may have been exposed — pasted into a ticket, captured in a screenshot, committed by accident.',
        example: 'waveqa credentials rotate',
      },
      {
        name: 'waveqa logout',
        summary: 'Forget the stored credentials, keep everything else.',
        detail: 'Your projects, specifications, evidence and reports are untouched. Run waveqa setup to sign back in.',
        example: 'waveqa logout',
      },
      {
        name: 'waveqa uninstall',
        summary: 'Remove the agent from this machine.',
        detail:
          'Deletes the stored client secret, the LLM API key and the configuration directory. Your working folder is kept — it holds specifications, evidence and reports you wrote, and nothing can regenerate them. --purge-projects deletes that too, and asks again before it does.',
        options: [{ flag: '--purge-projects', does: 'Also delete the working folder. Asks first.' }],
        example: 'waveqa uninstall',
      },
    ],
  },
];

function NewBadge() {
  return (
    <span className="ml-2 rounded-full border border-surge-400/40 bg-surge-400/10 px-2 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wider text-surge-300">
      New
    </span>
  );
}

function CommandCard({ command }: { command: Command }) {
  return (
    <Card className="mt-4">
      <h3 className="font-mono text-base font-semibold text-white">
        {command.name}
        {command.isNew && <NewBadge />}
      </h3>
      <p className="mt-2 text-sm font-medium text-hull-200">{command.summary}</p>
      <p className="mt-2 text-sm leading-relaxed text-hull-300">{command.detail}</p>

      {command.options && (
        <div className="mt-4">
          <ScrollTable>
            <thead>
              <tr className="border-b border-hull-700">
                <th className="py-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-hull-400">
                  Option
                </th>
                <th className="py-2.5 text-xs font-semibold uppercase tracking-wider text-hull-400">
                  What it does
                </th>
              </tr>
            </thead>
            <tbody>
              {command.options.map((option) => (
                <tr key={option.flag} className="border-b border-hull-800">
                  <td className="py-2.5 pr-4 align-top font-mono text-surge-300">
                    {option.flag}
                  </td>
                  <td className="py-2.5 text-hull-300">{option.does}</td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        </div>
      )}

      {command.example && (
        <CommandBlock className="mt-4" label={`Copy ${command.name}`}>
          {command.example}
        </CommandBlock>
      )}
    </Card>
  );
}

export function Commands() {
  usePageTitle('Command reference — WaveQA');

  return (
    <>
      <DocsNav />

      <Section className="border-b border-hull-800">
        <Eyebrow>Documentation</Eyebrow>
        <Heading level={1} className="text-white">
          Command reference
        </Heading>
        <Lead className="mt-5 max-w-2xl">
          Every WaveQA command, grouped by what you are trying to do rather than
          alphabetically. The same grouping is printed by{' '}
          <code className="font-mono text-surge-300">waveqa guide</code> in your
          terminal.
        </Lead>

        <div className="mt-8 flex flex-wrap gap-2">
          {GROUPS.map((group) => (
            <a
              key={group.id}
              href={`#${group.id}`}
              className={
                'rounded-lg border border-hull-700 px-3 py-1.5 text-sm text-hull-300 ' +
                'transition hover:border-hull-600 hover:text-white ' +
                'focus-visible:outline focus-visible:outline-2 ' +
                'focus-visible:outline-offset-2 focus-visible:outline-surge-400'
              }
            >
              {group.title}
            </a>
          ))}
        </div>

        <p className="mt-6 text-sm text-hull-400">
          New to WaveQA? <Link className="text-surge-400 hover:text-surge-300" to="/docs">Getting started</Link>{' '}
          walks the five commands that take you from an empty machine to a first
          report. The security and compliance flags are documented on{' '}
          <Link className="text-surge-400 hover:text-surge-300" to="/docs/security-compliance">
            Security &amp; Compliance
          </Link>
          .
        </p>
      </Section>

      {GROUPS.map((group, index) => (
        <Section
          key={group.id}
          id={group.id}
          className={index === GROUPS.length - 1 ? undefined : 'border-b border-hull-800'}
        >
          <Heading className="text-white">{group.title}</Heading>
          <Lead className="mt-3 max-w-2xl">{group.lead}</Lead>
          {group.commands.map((command) => (
            <CommandCard key={command.name} command={command} />
          ))}
        </Section>
      ))}
    </>
  );
}
